import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal, scrollToId } from "@/components/motion";
import { EditorialMarquee } from "@/components/EditorialMarquee";
import { AdvisorForm } from "@/components/AdvisorForm";
import { B, withBrand } from "@/components/BrandText";
import { usePageTitle } from "@/hooks/usePageTitle";

const activities = [
  "Consulting.",
  "Mentoring.",
  "Training.",
  "Troubleshooting.",
  "Evaluating.",
  "Assessments.",
  "Project work.",
  "Advising.",
];

const exampleBackgrounds = [
  "Former supervisors",
  "Tradespeople",
  "Manufacturing professionals",
  "Drivers and driver trainers",
  "Administrators",
  "Managers",
  "Maintenance professionals",
  "Small business owners",
  "Childcare providers",
  "Home and property experts",
  "Sales professionals",
  "Healthcare operations professionals",
  "Restaurant and hospitality professionals",
];

const clarityPoints = [
  "This is not another full-time job, and it's not another career.",
  "Advisors are independent — not employees of One More Shift.",
  "Submitting your information does not guarantee acceptance into the advisor network, engagements or income.",
  "You always decide which engagements, if any, are right for you.",
];

const advisorFaqs = [
  {
    q: "Do I need to be retired?",
    a: "No. One More Shift Advisors are welcome at any stage — retired, semi-retired, late-career or simply experienced. The network is centered around meaningful practical experience, not a specific age or retirement status.",
    testId: "advisor-faq-retired",
  },
  {
    q: "What kinds of experience are useful?",
    a: "Almost any deep, practical, real-world experience: industry, manufacturing, the trades, transportation, maintenance, operations, business management, administration, sales, hospitality, home and property, childcare and household operations, and more. Some of the most valuable experience doesn't fit neatly into a job title — tell us what you know.",
    testId: "advisor-faq-experience",
  },
  {
    q: "Am I an employee of One More Shift?",
    a: "No. Advisors are independent, not employees of One More Shift. Engagements are defined individually, and you decide which ones — if any — are right for you.",
    testId: "advisor-faq-employee",
  },
  {
    q: "Do I have to accept an engagement?",
    a: "Never. If we contact you about a request that seems to fit your background, you can simply say no. There are no commitments and no obligations.",
    testId: "advisor-faq-accept",
  },
  {
    q: "How does the process work?",
    a: "You tell us what you know. When someone brings One More Shift a problem, project, evaluation or training need that matches your background, we reach out, explain the situation and help define the engagement. You decide whether to take it.",
    testId: "advisor-faq-process",
  },
  {
    q: "How would compensation work?",
    a: "Every engagement is different. Before anything begins, the scope — including compensation — is discussed and agreed with you. One More Shift does not promise income or engagements; this is a founding-stage network being built carefully, one match at a time.",
    testId: "advisor-faq-compensation",
  },
  {
    q: "What happens after I submit my information?",
    a: "We review what you've shared and keep it in the founding advisor network. If a request arrives that fits your experience, we contact you. Nothing else happens, and you're not committed to anything.",
    testId: "advisor-faq-after",
  },
];

export default function ShareExperience() {
  usePageTitle(
    "I Have Experience | Become an Advisor | One More Shift",
    "Maybe you've got One More Shift. One More Shift is building a network of experienced independent advisors south and southeast of Houston who may be matched with defined problems, projects, evaluations, training needs and other engagements."
  );
  const [done, setDone] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="advisor-confirmation">
        <h1 className="text-3xl md:text-4xl font-bold">Thank you.</h1>
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
          We're building the founding <B /> network and will review your experience to see where it might fit.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="share-experience-page" className="overflow-x-hidden">
      <section className="bg-background" data-testid="advisor-hero-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-secondary font-semibold text-sm tracking-[0.3em] uppercase">I have experience</p>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold leading-tight" data-testid="share-experience-headline">
              Maybe you've got One More Shift.
            </h1>
            <p className="mt-5 text-2xl md:text-3xl font-serif font-bold text-primary leading-snug" data-testid="share-experience-value-line">
              You've spent years learning it. That knowledge still has value.
            </p>
            <p className="mt-6 text-lg text-foreground leading-relaxed max-w-3xl" data-testid="share-experience-intro">
              Years of practical experience — solving problems, training others, operating businesses, working in
              the trades, managing teams, working in industry, transportation, maintenance and operations — don't
              stop being valuable when a full-time career ends. <B /> is building a network of experienced
              independent advisors who may be matched with specific problems, projects, evaluations, training
              needs or other defined engagements.
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee />

      <section className="bg-card border-y border-border" data-testid="advisor-clarity-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold" data-testid="advisor-clarity-headline">
              To be clear about what this is.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-5" data-testid="share-clarity-list">
            {clarityPoints.map((point, i) => (
              <Reveal key={point} delay={i * 0.06}>
                <p className="text-xl font-semibold text-primary border-l-4 border-secondary pl-5 leading-relaxed">
                  {withBrand(point)}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background" data-testid="advisor-activities-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="advisor-activities-headline">
              What advisors might be asked to help with.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <ul
              className="mt-12 grid grid-cols-2 gap-x-8 gap-y-4 text-xl font-semibold text-primary max-w-2xl mx-auto"
              data-testid="advisor-activities-list"
            >
              {activities.map((a) => (
                <li key={a} className="border-l-4 border-secondary pl-4">{a}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="bg-card border-y border-border" data-testid="advisor-backgrounds-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="recruiting-headline">
              What have you spent your life learning?
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed text-center max-w-3xl mx-auto">
              We're looking for experienced people in Pearland, Friendswood, Alvin, League City, Webster, Dickinson
              and the Clear Lake area. A fancy title isn't what matters most. We want to know what you know.
            </p>
          </Reveal>
          <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="advisor-examples-list">
            {exampleBackgrounds.map((e, i) => (
              <Reveal as="li" key={e} delay={(i % 3) * 0.06}>
                <div className="bg-background border border-border rounded-md px-4 py-3 text-lg font-medium text-foreground transition-[border-color] duration-300 hover:border-secondary">
                  {e}
                </div>
              </Reveal>
            ))}
          </ul>
          <Reveal className="mt-8">
            <p className="text-lg text-center text-muted-foreground">
              Your experience may not fit this list.{" "}
              <button
                onClick={() => scrollToId("advisor-form")}
                data-testid="recruiting-tell-us-link"
                className="text-accent font-semibold underline underline-offset-4"
              >
                Tell us about it anyway.
              </button>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background" data-testid="advisor-faq-section">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-center" data-testid="advisor-faq-headline">
              Questions experienced people ask.
            </h2>
          </Reveal>
          <div className="mt-12 space-y-4">
            {advisorFaqs.map((f, i) => {
              const open = openIndex === i;
              return (
                <Reveal key={f.testId} delay={i * 0.04}>
                  <div className="bg-card border border-border rounded-lg" data-testid={f.testId}>
                    <button
                      data-testid={`${f.testId}-toggle`}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 text-xl font-semibold text-primary"
                      aria-expanded={open}
                      onClick={() => setOpenIndex(open ? null : i)}
                    >
                      {f.q}
                      <ChevronDown
                        size={28}
                        className={`shrink-0 text-secondary transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    {open && (
                      <p className="px-6 pb-6 text-lg text-foreground leading-relaxed" data-testid={`${f.testId}-answer`}>
                        {withBrand(f.a)}
                      </p>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground" data-testid="advisor-cta-band">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-bold text-white md:whitespace-nowrap" data-testid="advisor-cta-headline">
              You've got experience. Let's hear about it.
            </h2>
            <p className="mt-5 text-lg text-white/85 max-w-2xl mx-auto">
              Tell us what you know. If a fitting need comes in, we'll reach out.
            </p>
            <button
              onClick={() => scrollToId("advisor-form")}
              data-testid="advisor-cta-button"
              className="mt-10 inline-block bg-secondary text-secondary-foreground text-lg font-semibold px-10 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#caa95c] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
            >
              BECOME AN ADVISOR
            </button>
          </Reveal>
        </div>
      </section>

      <section className="bg-background scroll-mt-28" id="advisor-form" data-testid="advisor-form-section">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-bold" data-testid="advisor-form-headline">
              Tell us what you know.
            </h2>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              This isn't an application for another conventional job — there are no commitments, and joining the
              network does not guarantee engagements. We're getting to know who's out there.
            </p>
            <p
              className="mt-6 inline-block bg-card border-l-4 border-secondary px-5 py-3 text-lg font-semibold text-primary"
              data-testid="share-experience-founding-note"
            >
              Now building our founding local advisor network.
            </p>
          </Reveal>
          <AdvisorForm onSuccess={() => setDone(true)} />
        </div>
      </section>
    </div>
  );
}
